/**
 * VTA Push Notifications — client helpers.
 *
 * Provides:
 *   - getPermissionStatus()     → 'default' | 'granted' | 'denied' | 'unsupported'
 *   - subscribeUserToPush()     → asks permission then registers a PushSubscription
 *   - unsubscribeUserFromPush() → removes the subscription
 *   - playNotificationSound()   → short tone (WebAudio, ~0.4s)
 */
import { api } from "@/api/client";

/** URL-safe base64 → Uint8Array (for VAPID applicationServerKey). */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function getPermissionStatus() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

export async function subscribeUserToPush() {
  if (!isPushSupported()) throw new Error("Push notifications not supported in this browser.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("الإذن مرفوض من المتصفح");
  }
  // Fetch VAPID public key
  const { data } = await api.get("/push/vapid-public-key");
  const applicationServerKey = urlBase64ToUint8Array(data.public_key);

  // Service worker is registered in serviceWorkerRegistration.js — wait for it
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  }
  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: json.keys,
    user_agent: navigator.userAgent,
  });
  return sub;
}

export async function unsubscribeUserFromPush() {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  try {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
  } catch { /* keep removing locally even if API fails */ }
  await sub.unsubscribe();
}

/** Short notification chime via WebAudio — works without bundling an audio file. */
export function playNotificationSound() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const tones = [880, 1320]; // two-note chime
    tones.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      osc.connect(gain).connect(ctx.destination);
      const t0 = now + i * 0.12;
      gain.gain.setValueAtTime(0.001, t0);
      gain.gain.exponentialRampToValueAtTime(0.12, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.22);
      osc.start(t0);
      osc.stop(t0 + 0.24);
    });
    setTimeout(() => ctx.close(), 600);
  } catch { /* user-gesture restrictions — silent failure is fine */ }
}

/** Listen to messages from the service worker (push received / navigate). */
export function attachPushBridge(onPushReceived, onNavigate) {
  if (!isPushSupported()) return () => undefined;
  const handler = (event) => {
    const data = event.data || {};
    if (data.type === "VTA_PUSH_RECEIVED") {
      try { onPushReceived?.(data.payload); } catch { /* noop */ }
    } else if (data.type === "VTA_NAVIGATE") {
      try { onNavigate?.(data.link); } catch { /* noop */ }
    }
  };
  navigator.serviceWorker.addEventListener("message", handler);
  return () => navigator.serviceWorker.removeEventListener("message", handler);
}
